'use client';

import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import cx from 'clsx';
import Image from 'next/image';

import Link from '@/components/Link';
import ListItem from '@/components/ListItem';
import { getFeatureStackIcon, getFeatureStackLabel } from '@/config/feature-stack.config';
import colors from '@/styles/themes/colors';
import { Feature } from '@/types/feature.type';

import styles from './FeatureCard.module.scss';

const FeatureCard = ({
  title,
  description,
  image,
  videoUrl,
  listItems,
  techStack,
  bulletColor = 'blue',
  reverse,
  link,
}: Feature & { reverse?: boolean }) => (
  <Grid
    container
    spacing={{ xs: 5, md: 4 }}
    sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
    className={cx(styles.container, { [styles.reverse]: reverse })}
  >
    <Grid container size={{ xs: 12, lg: 6 }}>
      <Stack className={styles.content}>
        {techStack && techStack.length > 0 && (
          <Stack direction="row" className={styles.chips}>
            {techStack.map(tech => (
              <Chip
                key={tech}
                variant="filled"
                label={getFeatureStackLabel(tech)}
                icon={getFeatureStackIcon(tech)}
                className={styles.chip}
              />
            ))}
          </Stack>
        )}
        <Typography
          component="h2"
          variant="h5"
          sx={{
            typography: {
              md: 'h3',
            },
          }}
          color={colors.white}
        >
          {title}
        </Typography>
        <Typography variant="body1" className={styles.description}>
          {description}
        </Typography>
        {listItems && listItems.length > 0 && (
          <Stack component="ul" className={styles.list}>
            {listItems.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem key={index} text={item} variant={bulletColor} className={styles.listItem} />
            ))}
            {link && <Link href={link.href} text={link.text} />}
          </Stack>
        )}
      </Stack>
    </Grid>
    <Grid container size={{ xs: 12, lg: 6 }}>
      <Box className={cx(styles.boxWrapper, styles[bulletColor])}>
        {videoUrl ? (
          <video
            src={videoUrl}
            poster={image.src}
            autoPlay
            loop
            muted
            playsInline
            aria-label={description}
            className={styles.video}
          />
        ) : (
          <Image
            src={image.src}
            alt={image.alt}
            fill
            className={styles.image}
            sizes="(min-width: 768px) 560px, 100vw"
          />
        )}
      </Box>
    </Grid>
  </Grid>
);

export default FeatureCard;
